// Wrapper analytics : Google Analytics 4 + Meta Pixel en parité.
// Toute mesure passe par ce module, jamais par gtag/fbq directement, pour que
// les deux canaux restent alignés (même logique que emails + notifications côté backend).

import { metaTrack, newEventId, getMatchingParams, hasAdsConsent } from './metaPixel';
import { hasAnalyticsConsent } from './consent';

/** gtag.js n'est charge qu'apres consentement : sans lui, aucun appel GA4 ne part */
function gaReady() {
    return hasAnalyticsConsent() && typeof window !== 'undefined' && typeof window.gtag === 'function';
}

/** Correspondance des noms d'événements GA4 vers les événements standard Meta */
const META_EVENT_MAP = {
    page_view: 'PageView',
    view_item: 'ViewContent',
    add_to_cart: 'AddToCart',
    begin_checkout: 'InitiateCheckout',
    sign_up: 'CompleteRegistration',
    purchase: 'Purchase',
};

/**
 * Track Page View
 * @param {string} path - URL path (e.g. /commande/paiement)
 */
export function trackPage(path) {
    if (gaReady()) {
        window.gtag('event', 'page_view', {
            page_path: path
        });
    }

    metaTrack('PageView');
}

/**
 * Track Custom Event
 * @param {string} eventName - 'login', 'sign_up', 'add_to_cart'
 * @param {object} params - Additional data
 */
export function trackEvent(eventName, params = {}) {
    if (gaReady()) {
        window.gtag('event', eventName, params);
    }

    const metaEvent = META_EVENT_MAP[eventName];
    if (metaEvent) {
        metaTrack(metaEvent, {
            value: params.value,
            currency: params.currency || 'EUR',
        });
    }
}

/**
 * Track Purchase (Conversion)
 * Envoie l'achat au pixel navigateur puis à la Conversions API côté serveur,
 * avec le même eventId : Meta déduplique et récupère les conversions perdues
 * par les bloqueurs et les restrictions iOS.
 *
 * @param {object} order - { id, revenue, currency, items: [] }
 */
export function trackPurchase(order) {
    // GA4 Purchase
    if (gaReady()) {
        window.gtag('event', 'purchase', {
            transaction_id: order.id,
            value: order.revenue,
            currency: order.currency || 'EUR',
            items: order.items || []
        });
    }

    if (!hasAdsConsent()) return;

    const eventId = newEventId();

    metaTrack('Purchase', {
        value: order.revenue,
        currency: order.currency || 'EUR',
        contents: (order.items || []).map(i => ({
            id: i.item_id,
            quantity: i.quantity || 1,
            item_price: i.price,
        })),
        content_type: 'product',
    }, eventId);

    sendServerConversion(order.id, eventId);
}

/**
 * Double l'événement d'achat côté serveur (Conversions API).
 * Silencieux en cas d'échec : la mesure ne doit jamais casser le parcours client.
 */
async function sendServerConversion(orderId, eventId) {
    if (!orderId) return;
    try {
        const [{ httpsCallable }, { functions }] = await Promise.all([
            import('firebase/functions'),
            import('../lib/firebase'),
        ]);
        const send = httpsCallable(functions, 'sendMetaConversion');
        await send({ orderId, eventId, ...getMatchingParams() });
    } catch (e) {
        console.warn('[analytics] Conversions API indisponible', e?.message || e);
    }
}
